import { mulberry32, seedFrom } from "./random.js";
/**
 * The thirty-two letters, in dictionary order.
 *
 * **The index of a letter in this array is recorded in every trial** — `trial_start` stores a
 * stimulus as integers, so a letter reaches the event record as its position here. Reordering
 * the array therefore silently rewrites history: a recorded trial about ب would start reading
 * as a trial about پ. Letters may be appended; nothing may be moved or removed without a new
 * content bundle, which is the same rule that protects every other stimulus in `@talent/tasks`.
 */
const ALPHABET = [
    { char: "ا", shapeClass: "stroke", markGroup: null, joinsForward: false },
    { char: "ب", shapeClass: "bowl", markGroup: 1, joinsForward: true },
    { char: "پ", shapeClass: "bowl", markGroup: 1, joinsForward: true },
    { char: "ت", shapeClass: "bowl", markGroup: 1, joinsForward: true },
    { char: "ث", shapeClass: "bowl", markGroup: 1, joinsForward: true },
    { char: "ج", shapeClass: "loop", markGroup: 2, joinsForward: true },
    { char: "چ", shapeClass: "loop", markGroup: 2, joinsForward: true },
    { char: "ح", shapeClass: "loop", markGroup: 2, joinsForward: true },
    { char: "خ", shapeClass: "loop", markGroup: 2, joinsForward: true },
    { char: "د", shapeClass: "curve", markGroup: 3, joinsForward: false },
    { char: "ذ", shapeClass: "curve", markGroup: 3, joinsForward: false },
    { char: "ر", shapeClass: "curve", markGroup: 4, joinsForward: false },
    { char: "ز", shapeClass: "curve", markGroup: 4, joinsForward: false },
    { char: "ژ", shapeClass: "curve", markGroup: 4, joinsForward: false },
    { char: "س", shapeClass: "tooth", markGroup: 5, joinsForward: true },
    { char: "ش", shapeClass: "tooth", markGroup: 5, joinsForward: true },
    { char: "ص", shapeClass: "tooth", markGroup: 6, joinsForward: true },
    { char: "ض", shapeClass: "tooth", markGroup: 6, joinsForward: true },
    { char: "ط", shapeClass: "flat", markGroup: 7, joinsForward: true },
    { char: "ظ", shapeClass: "flat", markGroup: 7, joinsForward: true },
    { char: "ع", shapeClass: "eye", markGroup: 8, joinsForward: true },
    { char: "غ", shapeClass: "eye", markGroup: 8, joinsForward: true },
    { char: "ف", shapeClass: "eye", markGroup: 9, joinsForward: true },
    { char: "ق", shapeClass: "eye", markGroup: 9, joinsForward: true },
    { char: "ک", shapeClass: "flat", markGroup: 10, joinsForward: true },
    { char: "گ", shapeClass: "flat", markGroup: 10, joinsForward: true },
    { char: "ل", shapeClass: "stroke", markGroup: null, joinsForward: true },
    { char: "م", shapeClass: "round", markGroup: null, joinsForward: true },
    /**
     * ن sits in the bowl family, and that is a judgement rather than a typographic fact.
     *
     * On its own it is a deeper bowl than ب and tells itself apart easily. Joined it does not:
     * نـ and بـ are one skeleton with the dot moved from below to above, which is exactly the
     * confusion the joined levels exist to practise. Filing it by its hardest case is the useful
     * choice — a distractor that is only sometimes hard is still a fair distractor.
     */
    { char: "ن", shapeClass: "bowl", markGroup: 1, joinsForward: true },
    { char: "و", shapeClass: "curve", markGroup: null, joinsForward: false },
    { char: "ه", shapeClass: "round", markGroup: null, joinsForward: true },
    /** ی, likewise: alone it has a long tail, joined it is the bowl with two dots under it. */
    { char: "ی", shapeClass: "bowl", markGroup: 1, joinsForward: true },
];
/** How many letters the alphabet has. Exported so a caller can bound an index it read back. */
export const LETTER_COUNT = ALPHABET.length;
/**
 * The positions, in the order their indices are recorded.
 *
 * Same rule as the alphabet: the index travels into the event record, so the order is fixed.
 */
export const POSITIONS = ["isolated", "initial", "medial", "final"];
/**
 * The tatweel, which is how a positional form is asked for.
 *
 * A letter's four shapes are not four characters — they are one character, shaped by what sits
 * beside it. The tatweel is a neighbour to sit beside it: a bare connecting stroke that joins on
 * both sides, so a letter written next to one takes the shape it would take in a word.
 *
 * **The zero-width joiner was tried first and is silently wrong.** U+200D is the textbook answer
 * and it works when the text is laid out in a right-to-left context. A canvas is not one: Pixi
 * rasterises text on a detached canvas whose direction defaults to left-to-right, and there a
 * TRAILING joiner is ignored while a leading one is honoured. The result is not an error or a
 * missing glyph — it is the isolated form, quietly, in the two levels that exist to teach the
 * initial and medial ones. Measured rather than guessed: with the default direction, `ب` and
 * `ب‍` come out at exactly the same advance width, and with the direction forced to RTL they
 * differ and the joined width matches the font's own initial glyph.
 *
 * The tatweel produces the same shapes with no dependence on direction at all, which is what
 * makes it the right answer here rather than a workaround — the renderer must not be able to
 * change the stimulus by changing a layout setting.
 *
 * It is also the notation Persian teaching materials use. A first-grade book introduces a letter
 * as its four shapes written with exactly these connecting strokes, so a child meets the same
 * thing on the screen as on the page — and the stroke is not noise, it is the part that says
 * "this is how it joins".
 *
 * Single codepoints throughout, rather than the Arabic Presentation Forms block: that block is
 * deprecated for text and Persian webfonts frequently omit it, so asking for U+FE91 gets a tofu
 * box on the phones that matter.
 */
const TATWEEL = "ـ";
/** The character or characters that render one letter in one position. */
export function letterFormText(letterIndex, positionIndex) {
    const letter = ALPHABET[letterIndex];
    const position = POSITIONS[positionIndex];
    if (!letter || !position) {
        throw new Error(`no letter form for (${letterIndex}, ${positionIndex})`);
    }
    if (!letter.joinsForward && (position === "initial" || position === "medial")) {
        throw new Error(`${letter.char} does not join forward, so it has no ${position} form — a trial asking ` +
            "for one would show a shape the language does not contain");
    }
    // The stroke goes on the side the neighbour would be on, in reading order: a letter at the
    // start of a word joins to what follows it, which in a right-to-left script is drawn to its left.
    switch (position) {
        case "isolated":
            return letter.char;
        case "initial":
            return `${letter.char}${TATWEEL}`;
        case "medial":
            return `${TATWEEL}${letter.char}${TATWEEL}`;
        case "final":
            return `${TATWEEL}${letter.char}`;
    }
}
/** The bare letter, unshaped. For a teacher-facing list, never for the canvas. */
export const letterChar = (letterIndex) => {
    const letter = ALPHABET[letterIndex];
    if (!letter)
        throw new Error(`no letter at index ${letterIndex}`);
    return letter.char;
};
/** Where a letter sits in the alphabet, or -1 for anything that is not one of the thirty-two. */
export const letterIndexOf = (char) => ALPHABET.findIndex((letter) => letter.char === char);
/**
 * The letters sharing this one's skeleton — the same drawing with the marks moved.
 *
 * Exported because the word game needs the same answer. "Which letters look confusingly alike" is
 * one fact about Persian and it belongs in one place: a second table would be a second opinion, and
 * the two games would start disagreeing about what a hard distractor is.
 */
export const lookalikeLetters = (letterIndex) => {
    const letter = ALPHABET[letterIndex];
    if (!letter || letter.markGroup === null)
        return [];
    return ALPHABET.reduce((out, other, index) => {
        if (index !== letterIndex && other.markGroup === letter.markGroup)
            out.push(index);
        return out;
    }, []);
};
/**
 * The twelve levels.
 *
 * Written out one by one rather than computed, for the same reason the arithmetic ladder is:
 * the sequence IS the curriculum, and anyone changing the order of teaching should have to
 * edit something that reads like an order of teaching.
 *
 * Three ideas, in order:
 *
 *   1–4   *a letter has a shape.* From four letters that share nothing to four that share a
 *         silhouette. Nothing here needs reading and a four-year-old can start at level one.
 *   5–7   *the dots are part of the letter.* The cards close in on one skeleton, and by level
 *         seven all four are the same drawing with the marks moved. Level seven can only be
 *         the ب family and the ج family, because they are the only skeletons Persian writes
 *         six and four times over — not a limitation of the generator but the reason the level
 *         exists.
 *   8–12  *a letter keeps its name when it changes its shape.*
 *
 * **The joined levels run final, then initial, then medial, and that order is the point.** A
 * final form is the shape a child already knows with a stroke reaching back to the letter
 * before it — ـد is a د that has been joined to. An initial form drops the tail. A medial form
 * drops both ends and is the shape with least in common with the one the child learnt, which
 * is why it is last and why level eleven lands squarely on the two big families. Level twelve
 * turns the question around: a joined shape at the top, and the child names it among letters
 * standing alone, which is the direction reading a word actually asks for.
 */
function recipeFor(difficulty) {
    const four = { optionCount: 4, targetPosition: "isolated", optionPosition: "isolated" };
    switch (difficulty) {
        // Three cards, not four, and only here. A four-year-old's first trial of their first
        // sitting should be winnable by looking.
        case 1:
            return { ...four, optionCount: 3, confusable: 0, mates: 0 };
        case 2:
            return { ...four, confusable: 0, mates: 0 };
        case 3:
            return { ...four, confusable: 1, mates: 0 };
        case 4:
            return { ...four, confusable: 2, mates: 0 };
        case 5:
            return { ...four, confusable: 3, mates: 1 };
        case 6:
            return { ...four, confusable: 3, mates: 2 };
        case 7:
            return { ...four, confusable: 3, mates: 3 };
        /**
         * The first joined level is deliberately easy again.
         *
         * The child is being shown a new IDEA — that this unfamiliar shape is a letter they already
         * know — and pairing a new idea with the hardest distractors teaches neither. It is also
         * the only band where all thirty-two letters can appear, because a final form is the one
         * joined shape the seven non-joining letters have.
         */
        case 8:
            return { ...four, optionPosition: "final", confusable: 0, mates: 0 };
        case 9:
            return { ...four, optionPosition: "final", confusable: 2, mates: 0 };
        case 10:
            return { ...four, optionPosition: "initial", confusable: 3, mates: 1 };
        case 11:
            return { ...four, optionPosition: "medial", confusable: 3, mates: 2 };
        case 12:
            return { ...four, targetPosition: "joined", confusable: 3, mates: 1 };
        default:
            throw new Error(`no letter level ${difficulty}`);
    }
}
/**
 * How many trials in a row are about one letter before the alphabet moves on.
 *
 * Two, and the two numbers around it are what set it. A sitting is twelve trials, so two apiece
 * is six letters in an afternoon — a lesson a teacher could name. One apiece would be a parade
 * rather than practice, and three would mean a child who climbs a level mid-sitting has met four
 * letters all day.
 */
const TRIALS_PER_LETTER = 2;
/**
 * Where in the alphabet a sitting starts: **always at the beginning.**
 *
 * This briefly took an offset from the form, so that six forms across six days covered all
 * thirty-two letters — a child otherwise meets the first six letters and never reaches the last.
 * That was the wrong trade and it was made without asking. A class is taught ا first, and a teacher
 * who opens this game expects it to open on ا, every time. Starting a child somewhere in the middle
 * of the alphabet because it happened to be Thursday is not a curriculum.
 *
 * So the order is fixed and the VARIETY lives entirely in the cards instead: the form rotates every
 * sitting and seeds the wrong answers and their arrangement, so the same letter comes round with a
 * different set of cards each time. The letter is the lesson; the cards are the practice.
 *
 * The cost is real and worth stating: a twelve-trial sitting at two trials to a letter covers six
 * letters, so a child who only ever plays one sitting works on ا to ج and no further. Reaching the
 * end of the alphabet is the teacher's to drive, through the level they assign.
 */
const ALPHABET_START = 0;
/** Seeded Fisher-Yates. Returns a new array; the input is left alone. */
function shuffled(items, rand) {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = Math.floor(rand() * (i + 1));
        const a = out[i];
        const b = out[j];
        if (a === undefined || b === undefined)
            continue;
        out[i] = b;
        out[j] = a;
    }
    return out;
}
/** Whether a letter can be drawn in this position at all. */
const canStandIn = (letter, position) => letter.joinsForward || (position !== "initial" && position !== "medial");
export function generateLetterTrial(task, formId, trialIndex, difficulty) {
    const { minDifficulty, maxDifficulty } = task.measurement;
    if (difficulty < minDifficulty || difficulty > maxDifficulty) {
        throw new Error(`difficulty ${difficulty} outside ${minDifficulty}..${maxDifficulty} for ${task.id}`);
    }
    const recipe = recipeFor(difficulty);
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    /**
     * Which letters may appear at all on this level.
     *
     * The initial and medial levels exclude the seven letters that never join forward — ا د ذ ر
     * ز ژ و have no such shape, and inventing one would be showing a child something Persian
     * does not write. They come back at the final level, where ـا and ـد are real and are part
     * of the thing being taught.
     */
    const eligible = ALPHABET.map((letter, index) => ({ letter, index })).filter(({ letter }) => canStandIn(letter, recipe.optionPosition));
    /** The two inner rings around a candidate: same skeleton, and same silhouette but not. */
    const ringsAround = (candidate) => {
        const others = eligible.filter((o) => o.index !== candidate.index);
        const isMate = (o) => candidate.letter.markGroup !== null && o.letter.markGroup === candidate.letter.markGroup;
        return {
            mates: others.filter(isMate),
            near: others.filter((o) => o.letter.shapeClass === candidate.letter.shapeClass && !isMate(o)),
            far: others.filter((o) => o.letter.shapeClass !== candidate.letter.shapeClass),
        };
    };
    /**
     * The alphabet, walked. Two trials to a letter, always from the beginning.
     *
     * The one stimulus in the bundle that is not drawn: a class is taught ا, then ب, then پ, and a
     * teacher who spent the week on the first four does not want the fifth arriving by chance.
     */
    const cursor = ALPHABET_START + Math.floor(trialIndex / TRIALS_PER_LETTER);
    /**
     * The letter is whichever one the cursor is on. **The recipe does not get a say.**
     *
     * This used to work the other way round: the level stated how many same-skeleton distractors it
     * wanted, and the target was picked from the letters that could supply them. It reads as the
     * careful choice and it quietly broke the one rule this game has. ا has no twin, so on every level
     * that asks for one it was skipped — the alphabet opened on ب, and because two cursor positions
     * both fell through to it, the sitting began with four ب trials in a row.
     *
     * The order is the curriculum and cannot bend, so the recipe bends instead: the letter comes from
     * the cursor, and whatever distractors it can support are what the trial gets. For ا that means an
     * easier trial than the level nominally describes — which is honest, because ا genuinely has no
     * hard version of this question.
     *
     * **The cursor steps through the letters this level can show, not through the alphabet index.**
     * Seven letters never join forward, so the initial and medial levels have no shape for them and
     * must skip them. Reading the cursor as an alphabet position and taking "the next letter at or
     * after it" collapsed the skipped positions onto their neighbour — level eleven opened with four
     * ب trials in a row, because ا and its cursor position both landed there. Counting through the
     * serviceable letters instead keeps two trials per letter exactly, and the order stays
     * alphabetical because the list is.
     */
    const chosen = eligible[cursor % eligible.length];
    if (!chosen)
        throw new Error(`no letter can stand in ${recipe.optionPosition}`);
    /**
     * The target's position, for the level that reverses the question.
     *
     * Level 12 shows a joined shape and asks which letter it is, so the position is drawn from
     * the ones this particular letter actually has — three for most, one for the seven that
     * never join forward, and never a shape the language does not contain.
     */
    const joinedPositions = chosen.letter.joinsForward
        ? ["initial", "medial", "final"]
        : ["final"];
    const targetPosition = recipe.targetPosition === "joined"
        ? (shuffled(joinedPositions, rand)[0] ?? "final")
        : recipe.targetPosition;
    const rings = ringsAround(chosen);
    const mates = shuffled(rings.mates, rand);
    const near = shuffled(rings.near, rand);
    const far = shuffled(rings.far, rand);
    /**
     * The skeleton floor first, then the rest of the confusable quota, then the easy answers.
     *
     * Each shortfall falls into the NEXT list rather than being refused, which is what makes a
     * trial buildable for every letter on every level. Refusing instead would mean half the
     * alphabet could never be a target above level five — the ladder quietly excluding the
     * letters a child is most likely to be working on.
     *
     * `near` comes before `mates` in the second pass on purpose. A level that wanted one mate
     * and two lookalikes, on a letter with only one lookalike, should reach for another mate
     * rather than an easy answer: overshooting the ring is a slightly harder trial, and
     * undershooting it is a level that stopped teaching what it says it teaches.
     */
    const picked = [];
    const taken = new Set();
    const takeFrom = (ring, count) => {
        let left = count;
        for (const candidate of ring) {
            if (left <= 0 || picked.length >= recipe.optionCount - 1)
                return;
            if (taken.has(candidate.index))
                continue;
            taken.add(candidate.index);
            picked.push(candidate);
            left--;
        }
    };
    takeFrom(mates, recipe.mates);
    takeFrom(near, recipe.confusable - picked.length);
    takeFrom(mates, recipe.confusable - picked.length);
    takeFrom(far, recipe.optionCount - 1 - picked.length);
    // Anything still missing, from wherever it can be had.
    for (const ring of [near, mates, far])
        takeFrom(ring, recipe.optionCount);
    if (picked.length < recipe.optionCount - 1) {
        throw new Error(`only ${picked.length} distractors for ${chosen.letter.char} at level ${difficulty} — a ` +
            "trial with too few wrong answers must never reach a child");
    }
    /**
     * The cards, shuffled as a whole — not the answer dropped into a fixed line of wrong ones.
     *
     * **The first version placed the answer at a drawn index and left the distractors in the order
     * they were picked, which is a real leak.** They are picked ring by ring: same skeleton first,
     * then same silhouette, then the rest. So the hardest distractor was always the earliest wrong
     * card, and a child who noticed that — and five-year-olds notice exactly this kind of thing —
     * could learn the layout instead of the letter. Shuffling everything together is what makes
     * the position carry no information at all.
     *
     * The target is still a letter the child must recognise; nothing about where it lands should
     * help them find it.
     */
    const shownAs = POSITIONS.indexOf(recipe.optionPosition);
    const cards = shuffled([chosen, ...picked].map((o) => ({ letter: o.index, isAnswer: o.index === chosen.index })), rand);
    const correctIndex = cards.findIndex((card) => card.isAnswer);
    return {
        target: [chosen.index, POSITIONS.indexOf(targetPosition)],
        options: cards.map((card) => [card.letter, shownAs]),
        correctIndex,
    };
}
/**
 * Letters scoring: the chosen card must be the target's letter.
 *
 * Compared by index rather than by position, because at the joined levels the target and the
 * answer are deliberately DIFFERENT shapes of the same letter — which is the whole question
 * being asked, and comparing the pairs would score every correct answer as wrong.
 */
export function scoreLetterTrial(correctIndex, chosenIndex) {
    return correctIndex === chosenIndex;
}
//# sourceMappingURL=letters.js.map